import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import axiosInstance from '@/shared/utils/axios'
import router from '@/core/router'
import CryptoJS from 'crypto-js'
import axios from 'axios'
import { unsealUserPermissions, sealEnvelope, openEnvelope } from '@/core/auth/payloadCrypto'

const ENCRYPTION_KEY = 'FJKAIDFAJERKWJFKLASDJFAKSLFJLKFJLSDFHJLSHDF'

const encryptData = (data: string): string => {
  return CryptoJS.AES.encrypt(data, ENCRYPTION_KEY).toString()
}

const decryptData = (encryptedData: string): string => {
  const bytes = CryptoJS.AES.decrypt(encryptedData, ENCRYPTION_KEY)
  return bytes.toString(CryptoJS.enc.Utf8)
}

async function applySealedUser(rawUser: any, accessToken: string | null | undefined) {
  if (!rawUser) return null
  const permissions = await unsealUserPermissions(rawUser, accessToken)
  const { permissionsEnc: _drop, ...rest } = rawUser
  return {
    ...rest,
    permissions,
  }
}

export const useAuthStore = defineStore('auth', () => {
  const token = ref<string | null>(null)
  const user = ref<any | null>(null)
  const isRefreshing = ref(false)
  const isLoggingOut = ref(false)
  const refreshPromise = ref<Promise<string | null> | null>(null)
  const refreshRetryCount = ref(0)
  const MAX_REFRESH_RETRIES = 3
  
  // Session timeout configuration
  const SESSION_TIMEOUT_MS = 45 * 60 * 1000 // 45 minutes
  let sessionTimeoutId: ReturnType<typeof setTimeout> | null = null
  let inactivityTimeoutId: ReturnType<typeof setTimeout> | null = null
  let sessionListenersRegistered = false
  let boundResetTimeout: (() => void) | null = null

  const isAuthenticated = computed(() => !!token.value)

  const saveUserToLocalStorage = (userData: any) => {
    const encryptedUser = encryptData(JSON.stringify(userData))
    localStorage.setItem('user', encryptedUser)
  }

  const getUserFromLocalStorage = (): any | null => {
    const encryptedUser = localStorage.getItem('user')
    if (encryptedUser) {
      try {
        return JSON.parse(decryptData(encryptedUser))
      } catch (error) {
        console.error('Error decrypting user data:', error)
        return null
      }
    }
    return null
  }

  const login = async (email: string, password: string) => {
    try {
      console.log('Attempting login to:', `${import.meta.env.VITE_API_URL}/api/users/login`);

      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/api/users/login`,
        { email, password },
        {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        token.value = response.data.accessToken;
        user.value = await applySealedUser(response.data.user, response.data.accessToken);

        if (token.value) {
          localStorage.setItem('token', token.value);
        }
        if (user.value) {
          saveUserToLocalStorage(user.value);
        }

        // Setup session timeout after successful login
        setupSessionTimeoutListeners()

        return {
          success: true,
          message: response.data.message
        };
      } else {
        return {
          success: false,
          message: response.data.message || 'Login failed'
        };
      }
    } catch (error: any) {
      console.error('Login error details:', {
 message: error.message,
 response: error.response?.data,
 status: error.response?.status
 });

      // Handle different types of errors
      let errorMessage = 'Login failed';
      if (error.code === 'ERR_NETWORK') {
        errorMessage = 'Unable to connect to the server. Please check if the server is running and try again.';
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }

      return {
        success: false,
        message: errorMessage
      };
    }
  };

  const clearSessionTimeout = (removeListeners = false) => {
    if (sessionTimeoutId) {
      clearTimeout(sessionTimeoutId)
      sessionTimeoutId = null
    }
    if (inactivityTimeoutId) {
      clearTimeout(inactivityTimeoutId)
      inactivityTimeoutId = null
    }
    if (removeListeners && boundResetTimeout) {
      const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click']
      events.forEach(event => {
        window.removeEventListener(event, boundResetTimeout!, true)
      })
      boundResetTimeout = null
      sessionListenersRegistered = false
    }
  }

  const resetSessionTimeout = () => {
    clearSessionTimeout()
    sessionTimeoutId = setTimeout(() => {
      console.log(' Session timeout: 45 minutes elapsed')
      logout()
    }, SESSION_TIMEOUT_MS)
  }

  const setupSessionTimeoutListeners = () => {
    if (typeof window === 'undefined') return
    // Guard: only register listeners once to avoid stacking duplicates
    if (sessionListenersRegistered) {
      resetSessionTimeout() // Just reset the timer
      return
    }
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click']
    boundResetTimeout = () => resetSessionTimeout()
    events.forEach(event => {
      window.addEventListener(event, boundResetTimeout!, true)
    })
    sessionListenersRegistered = true
    resetSessionTimeout()
    console.log('Session timeout listeners setup (45 min timeout)')
  }

  const logout = async () => {
    console.log('Logout initiated...')
    
    // Clear session timeout timers AND remove event listeners
    clearSessionTimeout(true)

    // FIRST: Set logout state immediately
    isLoggingOut.value = true

    // SECOND: Clear all local authentication data immediately
    clearAllAuthData()

    // SECOND: Immediately navigate (don't wait for server response)
    console.log('Immediate navigation to login page...')
    const navigationPromise = router.push('/auth/signin').catch((routerError) => {
      console.error('Router navigation failed, using window.location:', routerError)
      window.location.href = '/auth/signin'
    })

    // THIRD: Try to call server logout in parallel (non-blocking)
    const serverLogoutPromise = (async () => {
      try {
        console.log('Calling server logout in background...')
        await axiosInstance.post('/api/users/logout', {}, {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json'
          },
          timeout: 3000 // Reduced timeout for faster UI response
        })
        console.log('Server logout successful')
      } catch (error) {
        console.warn('Server logout failed (local logout already completed):', error)
      }
    })()

    // Wait for navigation to complete, but don't wait for server logout
    await navigationPromise

    // Optional: Force immediate redirect if still on protected route
    if (window.location.pathname !== '/auth/signin' &&
      !window.location.pathname.startsWith('/auth/')) {
      console.log('Still on protected route, forcing immediate redirect...')
      window.location.href = '/auth/signin'
    }

    console.log('Logout UI process completed')

    // Reset logout state
    isLoggingOut.value = false

    // Server logout continues in background (non-blocking)
    serverLogoutPromise.catch(() => {
      // Already handled above, just prevent unhandled promise rejection
    })
  }

  const refreshToken = async (retryAttempt: number = 0): Promise<string | null> => {
    // Return existing refresh promise if already refreshing
    if (isRefreshing.value && refreshPromise.value) {
      console.log('Token refresh already in progress, waiting...');
      return refreshPromise.value
    }

    isRefreshing.value = true
    refreshPromise.value = new Promise(async (resolve) => {
      try {
        console.log(`Attempting token refresh (attempt ${retryAttempt + 1}/${MAX_REFRESH_RETRIES})...`);

        const response = await axios.post(
          `${import.meta.env.VITE_API_URL}/api/users/refresh-token`,
          {},
          {
            withCredentials: true,
            headers: {
              'Content-Type': 'application/json'
            },
            timeout: 10000 // 10 second timeout
          }
        );

        if (response.data.accessToken) {
          console.log('Token refresh successful');
          token.value = response.data.accessToken;
          localStorage.setItem('token', response.data.accessToken);

          if (response.data.user) {
            user.value = await applySealedUser(response.data.user, response.data.accessToken);
            saveUserToLocalStorage(user.value);
          }

          // Reset retry count on success
          refreshRetryCount.value = 0;
          resolve(response.data.accessToken);
        } else {
          console.error('No access token in refresh response');
          // Only logout after max retries
          if (retryAttempt >= MAX_REFRESH_RETRIES - 1) {
            console.error('Max refresh retries reached, logging out');
            await logout();
            resolve(null);
          } else {
            // Retry with exponential backoff
            const delay = Math.min(1000 * Math.pow(2, retryAttempt), 5000);
            console.log(`Retrying refresh after ${delay}ms...`);
            await new Promise(r => setTimeout(r, delay));
            isRefreshing.value = false;
            refreshPromise.value = null;
            const result = await refreshToken(retryAttempt + 1);
            resolve(result);
          }
        }
      } catch (error: any) {
        console.error('Token refresh error:', {
 message: error.message,
 status: error.response?.status,
 data: error.response?.data
 });

        // Handle network errors differently from auth errors
        const isNetworkError = error.code === 'ERR_NETWORK' || error.code === 'ECONNREFUSED';
        const isTimeoutError = error.code === 'ECONNABORTED';
        const isServerError = error.response?.status >= 500;

        // Retry on transient errors
        if ((isNetworkError || isTimeoutError || isServerError) && retryAttempt < MAX_REFRESH_RETRIES - 1) {
          const delay = Math.min(1000 * Math.pow(2, retryAttempt), 5000);
          console.log(`Transient error detected, retrying after ${delay}ms...`);
          await new Promise(r => setTimeout(r, delay));
          isRefreshing.value = false;
          refreshPromise.value = null;
          const result = await refreshToken(retryAttempt + 1);
          resolve(result);
        } else {
          // Auth errors (401, 403) or max retries reached - logout
          console.error('Auth error or max retries reached, logging out');
          await logout();
          resolve(null);
        }
      } finally {
        isRefreshing.value = false;
        refreshPromise.value = null;
      }
    });

    return refreshPromise.value;
  }

  const clearAllAuthData = () => {
    console.log('Performing complete auth data cleanup...')

    // Clear store data
    token.value = null
    user.value = null
    isRefreshing.value = false
    isLoggingOut.value = false
    refreshPromise.value = null

    // Clear all storage
    localStorage.clear()
    sessionStorage.clear()

    // Clear cookies
    const domain = window.location.hostname
    const allCookies = document.cookie.split(';')

    allCookies.forEach(cookie => {
      const eqPos = cookie.indexOf('=')
      const cookieName = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim()

      if (cookieName) {
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=${domain};`

        if (domain !== 'localhost' && !domain.includes('127.0.0.1')) {
          document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.${domain};`
        }
      }
    })

    console.log('Auth data cleanup completed')
  }

  const fetchProfile = async () => {
    try {
      const accessToken = token.value || localStorage.getItem('token')
      if (!accessToken) return

      const encryptedBody = await sealEnvelope({ t: Date.now() }, accessToken)
      const response = await axiosInstance.post('/api/users/profile', encryptedBody, {
        skipPermissionCheck: true
      } as any)

      const opened = await openEnvelope<{ user?: any }>(response.data, accessToken)
      if (opened?.user) {
        // Full profile is already plaintext after decrypt (includes permissions)
        const nextUser = {
          ...user.value,
          ...opened.user,
          permissions: opened.user.permissions || {},
        }
        delete (nextUser as any).permissionsEnc
        user.value = nextUser
        saveUserToLocalStorage(user.value)
      }
    } catch (error) {
      console.error('Failed to fetch user profile:', error)
    }
  }

  const checkAuth = () => {
    try {
      // Check if we're on an invitation signup page - if so, don't load auth from storage
      const currentUrl = window.location.href
      const hasInvitationToken = currentUrl.includes('/auth/signup?token=')
      
      if (hasInvitationToken) {
        console.log('Invitation signup detected in checkAuth - skipping auth check')
        return false
      }
      
      const storedToken = localStorage.getItem('token')
      const storedUser = getUserFromLocalStorage()

      if (storedToken && storedUser) {
        // Validate token format (basic check)
        if (typeof storedToken === 'string' && storedToken.length > 10) {
          token.value = storedToken
          user.value = storedUser
          // Permissions come from encrypted profile/login payload (not JWT)
          if (user.value && (!user.value.permissions || typeof user.value.permissions !== 'object')) {
            user.value.permissions = {}
          }
          // Setup session timeout for already authenticated users
          setupSessionTimeoutListeners()
          // console.log('Auth check successful - user authenticated')
          return true
        } else {
          console.warn('Invalid token format, clearing auth data')
          clearAllAuthData()
        }
      } else {
        console.log('No valid auth data found')
      }
    } catch (error) {
      console.error('Error during auth check:', error)
      clearAllAuthData()
    }

    return false
  }

  const isLoggedIn = computed(() => {
    return !!localStorage.getItem('token')
  })

  const forgotPassword = async (email: string) => {
    try {
      // Use axios directly instead of axiosInstance to avoid auth headers
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/api/users/forgot-password`,
        { email },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      return {
        success: true,
        message: response.data.message || 'Password reset instructions have been sent to your email',
        email // Return email to be used in the OTP verification step
      };
    } catch (error: any) {
      console.error('Forgot password error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to process password reset request'
      };
    }
  };

  const verifyOtp = async (email: string, otp: string) => {
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/api/users/verify-otp`,
        { email, otp },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      return {
        success: true,
        resetToken: response.data.resetToken,
        message: response.data.message || 'OTP verified successfully'
      };
    } catch (error: any) {
      console.error('OTP verification error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to verify OTP'
      };
    }
  };

  const resetPassword = async (resetToken: string, newPassword: string) => {
    try {
      const response = await axios.post(
        `${import.meta.env.VITE_API_URL}/api/users/reset-password`,
        { resetToken, newPassword },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      return {
        success: true,
        message: response.data.message || 'Password reset successful'
      };
    } catch (error: any) {
      console.error('Password reset error:', error);
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to reset password'
      };
    }
  };

  return {
    token,
    user,
    isAuthenticated,
    isLoggingOut,
    login,
    logout,
    refreshToken,
    checkAuth,
    fetchProfile,
    clearAllAuthData,
    isLoggedIn,
    forgotPassword,
    verifyOtp,
    resetPassword,
    setupSessionTimeoutListeners,
    clearSessionTimeout
  }
})